import { useEffect, useState } from "react";
import EditorDialog from "./EditorDialog";
import { parsePersianNumber } from "../../utils/persianNumber";
import { compressImage } from "../../utils/imageCompression";
import { resolveMediaUrl } from "../../utils/mediaUrl";
import { errorMessage } from "../../utils/errorMessage";
import { uploadMenuItemImage } from "../../api/menu";

const emptyItem = { name: "", price: "", description: "", image: "" };
const acceptedImageTypes = ["image/jpeg", "image/png", "image/webp"];
// Photos are shrunk in the browser before upload, so larger originals are fine.
const maxImageSize = 15 * 1024 * 1024;

const ItemDialog = ({ open, onClose, item, onSubmit }) => {
  const [form, setForm] = useState(emptyItem);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(item ? { ...emptyItem, ...item } : emptyItem);
      setError("");
      setUploading(false);
    }
  }, [item, open]);

  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  // The photo is uploaded as soon as it's picked and the item keeps only its
  // path. Embedding the photo itself made menu saves grow with every photo
  // until the server rejected them.
  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!acceptedImageTypes.includes(file.type)) {
      setError("فرمت تصویر باید JPG، PNG یا WebP باشد.");
      return;
    }
    if (file.size > maxImageSize) {
      setError("حجم تصویر باید کمتر از ۱۵ مگابایت باشد.");
      return;
    }
    setError("");
    setUploading(true);
    try {
      const url = await uploadMenuItemImage(await compressImage(file));
      setField("image", url);
    } catch (uploadError) {
      setError(errorMessage(uploadError, "آپلود تصویر ناموفق بود. دوباره تلاش کنید."));
    } finally {
      setUploading(false);
    }
  };

  const submit = (event) => {
    event.preventDefault();
    if (uploading) return;
    if (!form.name.trim()) return setError("نام آیتم را وارد کنید.");
    if (parsePersianNumber(form.price) === null) return setError("قیمت معتبر وارد کنید.");
    onSubmit({ ...form, name: form.name.trim(), description: form.description.trim() });
    onClose();
  };

  return (
    <EditorDialog open={open} onClose={onClose} title={item ? "ویرایش آیتم" : "افزودن آیتم"}>
      <form onSubmit={submit} className="space-y-4 pt-1">
        <label className="block text-sm font-medium text-gray-700">نام آیتم
          <input autoFocus value={form.name} onChange={(event) => setField("name", event.target.value)} className="mt-2 w-full rounded-xl border border-gray-200 p-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
        </label>
        <label className="block text-sm font-medium text-gray-700">قیمت (تومان)
          <input inputMode="numeric" value={form.price} onChange={(event) => setField("price", event.target.value)} className="mt-2 w-full rounded-xl border border-gray-200 p-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
        </label>
        <label className="block text-sm font-medium text-gray-700">توضیحات
          <textarea rows="3" value={form.description} onChange={(event) => setField("description", event.target.value)} className="mt-2 w-full resize-y rounded-xl border border-gray-200 p-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
        </label>
        <div>
          <p className="mb-2 text-sm font-medium text-gray-700">تصویر آیتم</p>
          <label className="flex min-h-32 cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-dashed border-gray-300 bg-gray-50 text-center text-sm text-gray-500 hover:border-primary">
            <input type="file" className="sr-only" accept="image/jpeg,image/png,image/webp" onChange={uploadImage} disabled={uploading} />
            {uploading
              ? <span role="status">در حال آپلود تصویر…</span>
              : form.image
                ? <img src={resolveMediaUrl(form.image)} alt="پیش‌نمایش آیتم" className="h-36 w-full object-cover" />
                : "انتخاب تصویر JPG، PNG یا WebP (حداکثر ۱۵ مگابایت)"}
          </label>
          {form.image && !uploading && <button type="button" onClick={() => setField("image", "")} className="mt-2 text-sm text-red-600 hover:underline">حذف تصویر</button>}
        </div>
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="min-h-11 rounded-xl border border-gray-200 px-4 text-sm font-medium">انصراف</button>
          <button type="submit" disabled={uploading} className="min-h-11 rounded-xl bg-primary px-5 text-sm font-bold text-white hover:bg-[#e8731f] disabled:cursor-not-allowed disabled:opacity-60">ثبت آیتم</button>
        </div>
      </form>
    </EditorDialog>
  );
};

export default ItemDialog;
